import { Component, Input, Output, OnDestroy, OnInit, Inject, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AppService } from 'app/app.service';
import { NotificationSeverity, UserAdminService } from 'app/services';
import { ConfirmationDialogService } from './../../../services/utility/confirmation-dialog.service';
import { NotificationService } from '../../../services/notification.service';
import { MrsColor } from 'app/enums';
import { StatusEnum } from './../../..//enums/global/status.enum';
import { Subject } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { UserFormState } from 'app/enums/global/userprofile-userform-state.enum'

@Component({
    selector: 'app-edit-user-profile',
    templateUrl: './edit-user-profile.component.html',
    styleUrls: ['./edit-user-profile.component.scss']
})
export class EditUsersProfileComponent implements OnInit, OnDestroy {

    @Input() selectedUser: any;
    @Input() accounts: any[];
    @Input() isReadOnly: boolean;
    @Output() formValidityChanged = new EventEmitter<boolean>()
    @Output() formSaveStateChanged = new EventEmitter<UserFormState>()

    userForm: UntypedFormGroup;
    originalValues: Record<string, any> = {};
    roles: any;
    enterpriseUser = false;
    showForm = false;
    userStatusToggleLabel: string;
    fieldTextType: boolean;
    userStatus: boolean
    private readonly _destroy$ = new Subject();
    private readonly subs = new SubSink();

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly userAdminService: UserAdminService,
        private readonly appService: AppService,
        private readonly confirmationService: ConfirmationDialogService,
        private readonly notificationService: NotificationService) {
    }

    ngOnInit(): void {
        this.userForm = this._fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', [Validators.required, Validators.email, this.emailDomainValidator()]],
            userRoleId: ['', Validators.required],
            sso: [''],
            password: ['', Validators.required],
            agentId: [''],
            statusId: '',
            userActivated: false
        });
        this.getRoles();
        this.setupForm();

        this.userForm.statusChanges.subscribe(status => {
            this.formValidityChanged.emit(this.userForm.valid);
          });
    }

    emailDomainValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
          const email = control.value;
          
          if (email && email.includes('@') && email.split('@')[1]?.includes('.')) {
            return null;
          }
          
          return { 'invalidDomain': true };
        };
    }

    setupForm() {
        if (this.selectedUser && this.selectedUser != null) {
            this.enterpriseUser = this.selectedUser.connectionType === 'Enterprise';

            this.userForm.patchValue({
                firstName: this.selectedUser.firstName,
                lastName: this.selectedUser.lastName,
                email: this.selectedUser.email,
                userRoleId: this.selectedUser.userRoleId,
                agentId: this.selectedUser.agentId,
                statusId: this.selectedUser.statusId,
                userActivated: this.selectedUser.statusId === StatusEnum.Active ? true : false,
            });
            this.userStatusToggleLabel = this.selectedUser.statusId === StatusEnum.Active ? 'User Activated' : 'User Deactivated';
            this.setupFields();

            this.showForm = true;

            this.userForm.get('password').setValidators([]);
        }
    }

    setupFields() {
        if (this.isReadOnly) {
            this.userForm.get('agentId').disable();
        }
        
        if (this.enterpriseUser || this.isReadOnly) {
            this.userForm.get('firstName').disable();
            this.userForm.get('lastName').disable();
            this.userForm.get('email').disable();
            this.userForm.get('userRoleId').disable();
            this.userForm.get('userActivated').disable();
        } else {
            this.userForm.get('firstName').enable();
            this.userForm.get('lastName').enable();
            this.userForm.get('email').enable();
            this.userForm.get('userRoleId').enable();
        }
    }

    onFieldFocus(fieldName: string): void {
        this.originalValues[fieldName] = this.userForm.get(fieldName)?.value;
        this.userForm.get(fieldName)?.markAsTouched();
    }

    onFieldBlur(fieldName: string): void {
        if (this.originalValues[fieldName] === this.userForm.get(fieldName)?.value)
            return;

        if (fieldName !== 'userActivated' && this.userForm.get(fieldName)?.valid) {
            this.formSaveStateChanged.emit(UserFormState.Saving); 
           this.autoSaveUser();
        }
    }

    autoSaveUser(): void {
        this.saveUser();
    }

    saveUser() {
        if (this.userForm.valid) {
            const user = {
                id: this.selectedUser?.id,
                firstName: this.userForm.get('firstName').value,
                lastName: this.userForm.get('lastName').value,
                email: this.userForm.get('email').value,
                userRoleId: this.userForm.get('userRoleId').value,
                password: this.userForm.get('password').value,
                agentId: this.userForm.get('agentId').value,
                connectionType: '',
                clients: this.selectedUser.clients,
                role: '',
                organization: '',
                statusId: this.userForm.get('statusId').value,
            };

            user.role = this.roles.find(x => x.id === user.userRoleId)?.roleName;

            if (this.enterpriseUser) {
                user.connectionType = 'Enterprise';
            } else {
                user.connectionType = 'Database';
            }

            if (user.clients.length > 1) {
                user.organization = 'MRS';
            }

            if (this.selectedUser && this.selectedUser !== null) {
                user.id = this.selectedUser.id;
                this.subs.add(this.userAdminService.editUserProfile(user, true).subscribe( user => {
                    if (user) {
                        this.formSaveStateChanged.emit(UserFormState.Saved); 
                    }
                    else {
                        this.formSaveStateChanged.emit(UserFormState.Error);
                    }
                }
                ));
            }
        }
    }

    getRoles() {
        this.subs.add(this.appService.getData('UserRoles').subscribe(
            data => {
                if (data.status === 'success') {
                    this.roles = data.data.sort((a, b) => a.roleName.localeCompare(b.roleName));
                } else {
                    this.roles = [];
                }
            }));
    }

    toggleUserStatus(event: any) {
        const isActive = this.selectedUser.statusId === StatusEnum.Active;
        const title = isActive ? 'Deactivate User?' : 'Reactivate User?';

        const name = this.selectedUser?.fullName;
        const successMessage = isActive
            ? `${name} has been deactivated.`
            : `${name} has been activated.`;
        const message = isActive
            ? `${name} will be deactivated. You will be able to reactivate them.`
            : `${name}'s account will be restored with their last account settings and permissions.`;

        this.confirmationService.open({
            title: title,
            message: message,
            okLabel: isActive ? 'DEACTIVATE USER' : 'REACTIVATE USER',
            cancelLabel: 'CANCEL',
            okTextColor: isActive ? MrsColor.UIRed : MrsColor.UIBlue,
            onOk: () => {
                this.subs.add(this.userAdminService.getUser(this.selectedUser?.id).pipe(
                    take(1),
                    mergeMap((user) => this.userAdminService.updateUserStatus({
                        userId: user.id,
                        statusId: user.statusId === StatusEnum.Active ? StatusEnum.Inactive : StatusEnum.Active
                    }))
                ).subscribe(() => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: `${successMessage}`,
                        actionLabel: 'CLOSE'
                    });
                }));
                this.selectedUser.statusId = !isActive ? StatusEnum.Active : StatusEnum.Inactive;
                this.userStatusToggleLabel = this.selectedUser.statusId === StatusEnum.Active ? 'User Activated' : 'User Deactivated';
            },
            showCancel: true,
            onCancel: () => {
                this.userForm.get('userActivated')?.setValue(!event.checked);
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}